import { api } from './api';
import { Patient, PatientData, PatientFilters, PatientStatistics } from '@/types/patient.types';
import { PaginatedResponse } from '@/types/api.types';

export const patientService = {
  // Get paginated list of patients
  getPatients: async (filters?: PatientFilters): Promise<PaginatedResponse<Patient>> => {
    const params: Record<string, any> = { ...filters };
    if (filters?.owned) {
      params.owned = 'true';
    }

    const response = await api.get<any>('/api/patients', { 
      params 
    });
    
    if (response.success) {
      // Map backend response structure to frontend expected structure
      const pagination = response.pagination || {
        page: 1,
        limit: 20,
        total: 0,
        pages: 0
      };
      
      return {
        data: response.data || [],
        page: pagination.page,
        limit: pagination.limit,
        total: pagination.total,
        pages: pagination.pages
      };
    }
    
    throw new Error(response.error || 'Failed to fetch patients');
  },

  // Create new patient
  createPatient: async (data: PatientData): Promise<Patient> => {
    const response = await api.post<any>('/api/patients', data);
    
    if (response.success && response.data) {
      // Backend returns { message: string, patient: Patient }
      return response.data.patient || response.data;
    }
    
    throw new Error(response.error || 'Failed to create patient');
  },

  // Get patient by ID
  getPatient: async (id: number): Promise<Patient> => {
    const response = await api.get<any>(`/api/patients/${id}`);

    if (response.success && response.data) {
      // Backend returns { patient: Patient }
      return response.data.patient || response.data;
    }

    throw new Error(response.error || 'Failed to fetch patient');
  },

  // Update patient
  updatePatient: async (id: number, data: PatientData): Promise<Patient> => {
    const response = await api.put<any>(`/api/patients/${id}`, data);
    
    if (response.success && response.data) {
      // Backend returns { message: string, patient: Patient }
      return response.data.patient || response.data;
    }
    
    throw new Error(response.error || 'Failed to update patient');
  },

  // Delete patient
  deletePatient: async (id: number): Promise<void> => {
    const response = await api.delete<any>(`/api/patients/${id}`);

    if (response.success) {
      return;
    }

    throw new Error(response.error || 'Failed to delete patient');
  },

  // Search patients
  searchPatients: async (query: string): Promise<Patient[]> => {
    const response = await api.get<any>('/api/patients/search', {
      params: { q: query }
    });

    if (response.success && response.data) {
      // Handle both paginated response and direct array
      if (Array.isArray(response.data)) {
        return response.data;
      }
      // Extract data array from paginated response
      return response.data.data || [];
    }

    throw new Error(response.error || 'Failed to search patients');
  },

  // Get patient by GUID
  getPatientByGuid: async (guid: string): Promise<Patient> => {
    const response = await api.get<Patient>(`/api/patients/guid/${guid}`);
    
    if (response.success && response.data) {
      return response.data;
    }
    
    throw new Error(response.error || 'Failed to fetch patient by GUID');
  },

  // Generate patient code for hospital
  generatePatientCode: async (hospitalId: number): Promise<string> => {
    const response = await api.get<{ code: string }>(`/api/patients/generate-code/${hospitalId}`);
    
    if (response.success && response.data) {
      return response.data.code;
    }
    
    throw new Error(response.error || 'Failed to generate patient code');
  },

  // Get patient statistics
  getStatistics: async (): Promise<PatientStatistics> => {
    const response = await api.get<PatientStatistics>('/api/patients/statistics');
    
    if (response.success && response.data) {
      return response.data;
    }
    
    throw new Error(response.error || 'Failed to fetch statistics');
  },

  // Get patient counts by hospital
  getCountsByHospital: async (): Promise<Array<{ hospital_id: number; hospital_name: string; count: number }>> => {
    const response = await api.get<Array<{ hospital_id: number; hospital_name: string; count: number }>>('/api/patients/counts/by-hospital');

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error || 'Failed to fetch hospital counts');
  },

  // Search patient by CNP for takeover validation
  searchPatientByCnp: async (cnp: string): Promise<Patient> => {
    const response = await api.get<any>('/api/patients/search-by-cnp', {
      params: { cnp },
    });

    if (response.success && response.data?.patient) {
      return response.data.patient as Patient;
    }

    throw new Error(response.error || 'Pacientul nu a fost găsit');
  },

  // Takeover a patient
  takeoverPatient: async (patientId: number, cnp: string): Promise<{ patient: Patient; message: string }> => {
    const response = await api.post<{ patient: Patient; message: string }>(`/api/patients/${patientId}/takeover`, { cnp });

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error || 'Nu s-a putut prelua pacientul');
  },

  // Get available patients for takeover
  getAvailableForTakeover: async (params?: { search?: string; page?: number; limit?: number }): Promise<PaginatedResponse<Patient>> => {
    const response = await api.get<any>('/api/patients/available-for-takeover', {
      params
    });

    if (response.success) {
      const pagination = response.pagination || {
        page: 1,
        limit: 20,
        total: 0,
        pages: 0
      };

      return {
        data: response.data || [],
        page: pagination.page,
        limit: pagination.limit,
        total: pagination.total,
        pages: pagination.pages
      };
    }

    throw new Error(response.error || 'Nu s-au putut încărca pacienții disponibili');
  },

  // Get owned patients
  getOwnedPatients: async (params?: { page?: number; limit?: number }): Promise<PaginatedResponse<Patient>> => {
    const response = await api.get<any>('/api/patients/owned', {
      params
    });

    if (response.success) {
      const pagination = response.pagination || {
        page: 1,
        limit: 20,
        total: 0,
        pages: 0
      };

      return {
        data: response.data || [],
        page: pagination.page,
        limit: pagination.limit,
        total: pagination.total,
        pages: pagination.pages
      };
    }

    throw new Error(response.error || 'Nu s-au putut încărca pacienții proprii');
  },
};
